const BACKEND_DISPATCH_STATUS_KEYS = new Set(["courant", "a_revalider", "livre", "cloture"]);

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeDispatchStatusToken(value) {
  const normalizedValue = normalizeInlineText(value).toLowerCase();
  return BACKEND_DISPATCH_STATUS_KEYS.has(normalizedValue) ? normalizedValue : "";
}

function getDispatchMonthKey(dispatchDate = "") {
  const normalizedDispatchDate = normalizeInlineText(dispatchDate);
  const match = /^(\d{4}-\d{2})-\d{2}$/.exec(normalizedDispatchDate);
  return match ? match[1] : "";
}

function normalizeDispatchMonthKey(dispatchMonth = "") {
  const normalizedDispatchMonth = normalizeInlineText(dispatchMonth);
  const match = /^(\d{4})-(\d{2})$/.exec(normalizedDispatchMonth);
  if (!match) {
    return "";
  }

  const month = Number(match[2]);
  if (!Number.isInteger(month) || month < 1 || month > 12) {
    return "";
  }

  return `${match[1]}-${match[2]}`;
}

function getOptionalResultsNumber(source, key) {
  const rawValue = source?.[key];
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return null;
  }

  const numericValue = Number(rawValue);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return null;
  }

  return Math.round(numericValue);
}

function readResultsNumber(item, camelKey, snakeKey) {
  const camelValue = getOptionalResultsNumber(item, camelKey);
  if (camelValue !== null) {
    return camelValue;
  }

  const snakeValue = getOptionalResultsNumber(item, snakeKey);
  if (snakeValue !== null) {
    return snakeValue;
  }

  return 0;
}

function readExplicitBoolean(item, camelKey, snakeKey) {
  if (!item || typeof item !== "object") {
    return null;
  }

  const camelValue = item?.[camelKey];
  if (camelValue === true || camelValue === false) {
    return camelValue;
  }

  const snakeValue = item?.[snakeKey];
  if (snakeValue === true || snakeValue === false) {
    return snakeValue;
  }

  return null;
}

function resolveActiveOrderCount(item = null) {
  const explicitActiveOrderCount = getOptionalResultsNumber(item, "activeOrderCount")
    ?? getOptionalResultsNumber(item, "active_order_count");
  if (explicitActiveOrderCount !== null) {
    return explicitActiveOrderCount;
  }

  const orderCount = readResultsNumber(item, "orderCount", "order_count");
  const cancelledCount = readResultsNumber(item, "cancelledCount", "cancelled_count");
  return Math.max(orderCount - cancelledCount, 0);
}

function getDispatchStatusLabel(statusKey = "") {
  if (statusKey === "courant") {
    return "Courante";
  }

  if (statusKey === "a_revalider") {
    return "À revalider";
  }

  if (statusKey === "livre") {
    return "Livrée";
  }

  if (statusKey === "cloture") {
    return "Fermée";
  }

  return "Indisponible";
}

function getLegacyStatusKey(statusKey = "", { hasSnapshot = false, isClosed = false, isInconsistent = false } = {}) {
  if (statusKey === "cloture" || isClosed) {
    return "closed";
  }

  if (statusKey === "a_revalider" || isInconsistent) {
    return "inconsistent";
  }

  if (statusKey === "livre") {
    return "optimized";
  }

  return hasSnapshot ? "optimized" : "unoptimized";
}

function resolveLegacyDispatchStatus(item = null) {
  const dispatchDayState = normalizeInlineText(item?.dispatchDayState ?? item?.dispatch_day_state).toLowerCase();
  const hasSnapshot = readExplicitBoolean(item, "hasSnapshot", "has_snapshot") === true;
  const isClosed = readExplicitBoolean(item, "isClosed", "is_closed") === true;
  const isInconsistent = readExplicitBoolean(item, "isInconsistent", "is_inconsistent") === true;
  const orderCount = readResultsNumber(item, "orderCount", "order_count");
  const activeOrderCount = resolveActiveOrderCount(item);
  const completedCount = readResultsNumber(item, "completedCount", "completed_count");
  const lastOptimizedAt = normalizeInlineText(item?.lastOptimizedAt ?? item?.last_optimized_at ?? item?.status?.lastOptimizedAt);

  if (isClosed || dispatchDayState === "closed" || dispatchDayState === "archived") {
    return {
      key: "cloture",
      reason: dispatchDayState === "archived" ? "dispatch-day-archived" : "dispatch-day-closed",
    };
  }

  if (activeOrderCount > 0 && completedCount === activeOrderCount) {
    return {
      key: "livre",
      reason: "all-active-orders-completed",
    };
  }

  if (isInconsistent) {
    return {
      key: "a_revalider",
      reason: "legacy-inconsistent-dispatch-day",
    };
  }

  if (orderCount === 0) {
    return {
      key: "courant",
      reason: "no-orders-for-dispatch-day",
    };
  }

  if (activeOrderCount === 0) {
    return {
      key: "courant",
      reason: "no-active-orders",
    };
  }

  return {
    key: "courant",
    reason: lastOptimizedAt || hasSnapshot ? "latest-run-current" : "never-optimized",
  };
}

function buildUnavailableDispatchStatus() {
  return {
    canOptimize: false,
    isFrozen: false,
    key: "unavailable",
    label: "Indisponible",
    lastOptimizedAt: null,
    legacyKey: "unavailable",
    reason: null,
    requiresRevalidation: false,
    source: "missing",
  };
}

function buildDispatchDayStatus(item = null) {
  if (!item || typeof item !== "object") {
    return buildUnavailableDispatchStatus();
  }

  const dispatchDayState = normalizeInlineText(item?.dispatchDayState ?? item?.dispatch_day_state).toLowerCase();
  const rawHasSnapshot = readExplicitBoolean(item, "hasSnapshot", "has_snapshot");
  const rawIsClosed = readExplicitBoolean(item, "isClosed", "is_closed");
  const rawIsInconsistent = readExplicitBoolean(item, "isInconsistent", "is_inconsistent");
  const rawCanOptimize = readExplicitBoolean(item, "canOptimize", "can_optimize");
  const rawIsFrozen = readExplicitBoolean(item, "isFrozen", "is_frozen");
  const rawRequiresRevalidation = readExplicitBoolean(item, "requiresRevalidation", "requires_revalidation");
  const lastOptimizedAt = normalizeInlineText(item?.lastOptimizedAt ?? item?.last_optimized_at ?? item?.status?.lastOptimizedAt) || null;
  const hasSnapshot = rawHasSnapshot === true || Boolean(lastOptimizedAt);
  const isClosed = rawIsClosed === true || dispatchDayState === "closed" || dispatchDayState === "archived";
  const isInconsistent = rawIsInconsistent === true;
  const normalizedBackendStatus = normalizeDispatchStatusToken(
    item?.dispatchStatus
    ?? item?.dispatch_status
    ?? item?.status?.key,
  );
  const legacyFallback = resolveLegacyDispatchStatus(item);
  const key = normalizedBackendStatus || legacyFallback.key;
  const reason = normalizeInlineText(
    item?.dispatchStatusReason
    ?? item?.dispatch_status_reason
    ?? item?.status?.reason,
  ) || legacyFallback.reason || null;
  const activeOrderCount = resolveActiveOrderCount(item);
  const canOptimize = rawCanOptimize ?? ((key === "courant" || key === "a_revalider") && activeOrderCount > 0);
  const isFrozen = rawIsFrozen ?? (key === "livre" || key === "cloture");
  // Une journée fermée est terminée : « À revalider » n'y est pas montré. Le marquage reste pour la réouverture.
  const requiresRevalidation = !isClosed && key !== "cloture" && (rawRequiresRevalidation ?? key === "a_revalider");

  return {
    canOptimize,
    isFrozen,
    key,
    label: getDispatchStatusLabel(key),
    lastOptimizedAt,
    legacyKey: getLegacyStatusKey(key, { hasSnapshot, isClosed, isInconsistent }),
    reason,
    requiresRevalidation,
    source: normalizedBackendStatus ? "backend" : "legacy",
  };
}

function normalizeDispatchDayItem(item = null, activeDispatchDate = "") {
  const dispatchDate = normalizeInlineText(item?.dispatchDate ?? item?.dispatch_date);
  const dispatchDayState = normalizeInlineText(item?.dispatchDayState ?? item?.dispatch_day_state).toLowerCase();
  const activeOrderCount = resolveActiveOrderCount(item);
  const assignedCount = readResultsNumber(item, "assignedCount", "assigned_count");
  const cancelledCount = readResultsNumber(item, "cancelledCount", "cancelled_count");
  const completedCount = readResultsNumber(item, "completedCount", "completed_count");
  const orderCount = readResultsNumber(item, "orderCount", "order_count");
  const readyCount = readResultsNumber(item, "readyCount", "ready_count");
  const status = buildDispatchDayStatus({
    ...item,
    activeOrderCount,
    assignedCount,
    cancelledCount,
    completedCount,
    dispatchDayState,
    orderCount,
    readyCount,
  });
  const hasSnapshot = readExplicitBoolean(item, "hasSnapshot", "has_snapshot") === true || Boolean(status.lastOptimizedAt);
  const isClosed = status.key === "cloture" || readExplicitBoolean(item, "isClosed", "is_closed") === true;
  const isInconsistent = readExplicitBoolean(item, "isInconsistent", "is_inconsistent") === true;

  return {
    activeOrderCount,
    assignedCount,
    canOptimize: status.canOptimize,
    cancelledCount,
    completedCount,
    dispatchDate,
    dispatchDayState,
    dispatchStatus: status.key,
    dispatchStatusLabel: status.label,
    dispatchStatusReason: status.reason,
    dispatchStatusSource: status.source,
    hasDispatch: readExplicitBoolean(item, "hasDispatch", "has_dispatch") === true,
    hasSnapshot,
    isActive: dispatchDate && dispatchDate === activeDispatchDate,
    isClosed,
    isFrozen: status.isFrozen,
    isInconsistent,
    label: normalizeInlineText(item?.label) || dispatchDate,
    lastOptimizedAt: status.lastOptimizedAt,
    legacyStatusKey: status.legacyKey,
    orderCount,
    readyCount,
    requiresRevalidation: status.requiresRevalidation,
    status,
  };
}

export function resolveDispatchDayStatus(item = null) {
  return buildDispatchDayStatus(item);
}

export function buildDispatchDayControlsModel({
  activeDispatchDate = "",
  activeDispatchMonth = "",
  activeDispatchState = "",
  dispatchDays = [],
  planningSummary = {},
  selectedLabel = "",
  showArchive = false,
} = {}) {
  const normalizedActiveDispatchDate = normalizeInlineText(activeDispatchDate);
  const normalizedActiveDispatchMonth = normalizeDispatchMonthKey(activeDispatchMonth)
    || getDispatchMonthKey(normalizedActiveDispatchDate);
  const normalizedItems = Array.isArray(dispatchDays)
    ? dispatchDays.filter(Boolean).map((item) => normalizeDispatchDayItem(item, normalizedActiveDispatchDate))
    : [];

  if (!normalizedItems.length) {
    return null;
  }

  const activeItem = normalizedItems.find((item) => item.dispatchDate === normalizedActiveDispatchDate) || null;
  const activeStatus = normalizedActiveDispatchDate
    ? (activeItem?.status || resolveDispatchDayStatus(planningSummary))
    : null;
  const activeDispatchMonthKey = normalizedActiveDispatchMonth;
  const isVisibleWithoutArchive = (item) => {
    if (!item?.isClosed) {
      return true;
    }

    if (item.dispatchDate === normalizedActiveDispatchDate) {
      return true;
    }

    return Boolean(activeDispatchMonthKey) && getDispatchMonthKey(item.dispatchDate) === activeDispatchMonthKey;
  };
  const hiddenClosedCount = normalizedItems.filter((item) => (
    item.isClosed
    && !isVisibleWithoutArchive(item)
  )).length;
  const visibleDispatchDays = normalizedItems.filter((item) => (
    showArchive
    || isVisibleWithoutArchive(item)
  ));

  return {
    activeCanOptimize: activeItem?.canOptimize ?? activeStatus?.canOptimize ?? false,
    activeDispatchDate: normalizedActiveDispatchDate,
    activeDispatchMonth: activeDispatchMonthKey,
    activeDispatchState: normalizeInlineText(activeDispatchState).toLowerCase()
      || normalizeInlineText(activeItem?.dispatchDayState).toLowerCase()
      || normalizeInlineText(planningSummary?.dispatch_day_state).toLowerCase(),
    activeDispatchStatus: activeItem?.dispatchStatus || activeStatus?.key || "",
    activeDispatchStatusReason: activeItem?.dispatchStatusReason || activeStatus?.reason || "",
    activeIsFrozen: activeItem?.isFrozen ?? activeStatus?.isFrozen ?? false,
    activeItem,
    activeLastOptimizedAt: activeItem?.lastOptimizedAt || activeStatus?.lastOptimizedAt || null,
    activeRequiresRevalidation: activeItem?.requiresRevalidation ?? activeStatus?.requiresRevalidation ?? false,
    activeStatus,
    hiddenClosedCount,
    items: visibleDispatchDays,
    selectedLabel: normalizeInlineText(selectedLabel)
      || normalizeInlineText(activeItem?.label)
      || normalizedActiveDispatchDate
      || "Journée active",
    showArchive: showArchive === true,
  };
}